import * as React from 'react';
import { Alert, Card, CardBody, CardTitle, ListGroup, ListGroupItem, Table } from 'reactstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization } from '../../utils/wrappers';
import { State } from '../../redux/state';
import * as selectors from '../../redux/selectors';
import { userApiBasePath } from '../../redux/selectors/userApi';
import Footer from '../../shared/Footer';
import RouteLink from '../../shared/RouteLink';
import {
  ADMIN_GUIDE_INTRO,
  ADMIN_GUIDE_QUICK_LINKS,
  AdminGuideSection,
  buildAdminGuideSections
} from '../../content/adminGuideContent';
import { ScheduledCommunicationsStatus } from '../Communications/scheduledTypes';
import { competitionTimeZone } from '../../utils/adminDateTime';
import { isStaging } from '../../utils/environment';
import { CompetitionPledges } from '../../utils/userApiHelper';
import { values } from 'lodash';
import './adminGuide.scss';

interface Props {
  canViewAdminGuide: boolean;
  apiBase: string;
  token: string;
  competitionName: string;
  competitionTimezone?: string;
  competitionStartsAt?: string;
  competitionEndsAt?: string;
  launchWelcomeAt?: string;
  heroPledge?: { points: number; repeats: number } | null;
  teamsLabel?: string;
  teamLabel?: string;
}

interface LocalState {
  scheduledStatus: ScheduledCommunicationsStatus | null;
  loadingTiming: boolean;
}

class AdminGuidePage extends React.Component<LProps<Props>, LocalState> {
  public state: LocalState = {
    scheduledStatus: null,
    loadingTiming: false
  };

  public componentDidMount() {
    this.loadTimingIfReady();
  }

  public componentDidUpdate() {
    this.loadTimingIfReady();
  }

  private loadTimingIfReady = () => {
    const { canViewAdminGuide, competitionName, token } = this.props;
    const { scheduledStatus, loadingTiming } = this.state;
    if (canViewAdminGuide && competitionName && token && !scheduledStatus && !loadingTiming) {
      this.loadTiming();
    }
  }

  private authHeaders = () => ({
    Authorization: this.props.token,
    Accept: 'application/json'
  })

  private loadTiming = async () => {
    const { apiBase, competitionName } = this.props;
    this.setState({ loadingTiming: true });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/communications/scheduled-status`,
        { headers: this.authHeaders() }
      );
      if (!res.ok) {
        this.setState({ scheduledStatus: null, loadingTiming: false });
        return;
      }
      const scheduledStatus = await res.json();
      this.setState({ scheduledStatus, loadingTiming: false });
    } catch (_err) {
      this.setState({ scheduledStatus: null, loadingTiming: false });
    }
  }

  private sections = (): AdminGuideSection[] => {
    const {
      scheduledStatus,
      heroPledge,
      teamsLabel,
      teamLabel,
      competitionStartsAt,
      competitionEndsAt,
      launchWelcomeAt,
      competitionTimezone
    } = this.props;
    const timing = scheduledStatus
      ? {
        competitionStart: scheduledStatus.competitionStart,
        competitionEnd: scheduledStatus.competitionEnd,
        launchWelcomeAt: scheduledStatus.launchWelcomeAt,
        timezone: scheduledStatus.timezone || competitionTimezone
      }
      : (competitionStartsAt
        ? {
          competitionStart: competitionStartsAt,
          competitionEnd: competitionEndsAt,
          launchWelcomeAt,
          timezone: competitionTimezone
        }
        : undefined);
    return buildAdminGuideSections({
      timing,
      isStaging,
      heroPledge,
      teamsLabel,
      teamLabel
    });
  }

  private renderMatrix = (section: AdminGuideSection) => {
    const { t } = this.props;
    if (!section.matrix || section.matrix.length === 0) {
      return null;
    }
    return (
      <Table responsive={true} bordered={true} size="sm" className="admin-guide__matrix">
        <thead>
          <tr>
            <th>{t('Message')}</th>
            <th>{t('When')}</th>
            <th>{t('Who receives it')}</th>
          </tr>
        </thead>
        <tbody>
          {section.matrix.map((row) => (
            <tr key={row.message}>
              <td>{t(row.message)}</td>
              <td>{t(row.when)}</td>
              <td>{t(row.who)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  public render() {
    const { t, canViewAdminGuide } = this.props;
    const sections = this.sections();

    if (!canViewAdminGuide) {
      return (
        <div className="admin-guide page-content">
          <Alert color="warning">
            {t('This guide is available to authorized Challenge administrators.')}
          </Alert>
          <Footer />
        </div>
      );
    }

    return (
      <div className="admin-guide page-content">
        <header className="admin-guide__header">
          <h1>{t('Challenge Admin Guide')}</h1>
          <p className="admin-guide__intro">{t(ADMIN_GUIDE_INTRO)}</p>
        </header>

        <nav className="admin-guide__quick-links" aria-label={t('Quick links')}>
          <h2 className="admin-guide__quick-links-title">{t('Quick links')}</h2>
          <ul className="admin-guide__quick-links-list">
            {ADMIN_GUIDE_QUICK_LINKS.map((link) => (
              <li key={link.route}>
                <RouteLink route={link.route}>{t(link.label)}</RouteLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="admin-guide__sections">
          {sections.map((section) => (
            <Card key={section.id} id={section.id} className="admin-guide__card">
              <CardBody>
                <CardTitle tag="h2">{t(section.title)}</CardTitle>
                <p>{t(section.intro)}</p>
                {section.id === 'communications' && this.state.loadingTiming &&
                  <p className="admin-guide__timing-note cc__lighttext">{t('Loading Challenge timing…')}</p>
                }
                {this.renderMatrix(section)}
                {section.bullets && section.bullets.length > 0 && (
                  <ul className="admin-guide__bullets">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{t(bullet)}</li>
                    ))}
                  </ul>
                )}
                {section.links && section.links.length > 0 && (
                  <ListGroup className="admin-guide__links">
                    {section.links.map((link) => (
                      <ListGroupItem key={link.route} className="admin-guide__link-item">
                        <RouteLink route={link.route}>{t(link.label)}</RouteLink>
                        {link.description &&
                          <span className="admin-guide__link-desc">{t(link.description)}</span>
                        }
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                )}
              </CardBody>
            </Card>
          ))}
        </div>

        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => {
  const competition = state.competition;
  const config = (competition && competition.config) || {};
  const heroPledge = state.competitionPledges
    ? values(state.competitionPledges).find((p) => p.uuid === CompetitionPledges.HighlightAHero)
    : undefined;
  return {
    canViewAdminGuide: selectors.canViewAdminTools(state),
    apiBase: userApiBasePath(state),
    token: state.token || '',
    competitionName: (competition && competition.name) || selectors.competitionName(state) || '',
    competitionTimezone: competitionTimeZone(competition as { timezone?: string } | null),
    heroPledge: heroPledge ? { points: heroPledge.points, repeats: heroPledge.repeats } : null,
    teamsLabel: config.teamsLabel,
    teamLabel: config.teamLabel,
    competitionStartsAt: competition && competition.startsAt,
    competitionEndsAt: competition && competition.endsAt,
    launchWelcomeAt: typeof config.launchWelcomeAt === 'string' ? config.launchWelcomeAt : undefined
  };
};

export default asPage({ requiresCompetitionStart: false, authenticated: true })(
  compose(
    withLocalization('adminGuide'),
    connect(mapStateToProps)
  )(AdminGuidePage)
);
